import React, { useEffect, useState } from 'react'
import { fetchAllDataAPI } from '../../utils/api'
import { toast } from 'react-toastify'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'

const COLORS = ['#4e9191', '#0e7c52', '#00CED1', '#c779db', '#597e0d', '#e67e22', '#e74c3c']
const RADIAN = Math.PI / 180;

const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, name, percent }) => {
    if (percent < 0.04) return null;

    const x1 = cx + (outerRadius + 5) * Math.cos(-midAngle * RADIAN);
    const y1 = cy + (outerRadius + 5) * Math.sin(-midAngle * RADIAN);
    const x2 = cx + (outerRadius + 22) * Math.cos(-midAngle * RADIAN);
    const y2 = cy + (outerRadius + 22) * Math.sin(-midAngle * RADIAN);
    const x3 = cx + (outerRadius + 35) * Math.cos(-midAngle * RADIAN);
    const y3 = cy + (outerRadius + 35) * Math.sin(-midAngle * RADIAN);

    return (
        <g>
            {/* Arrow line */}
            <defs>
                <marker id="arrowhead" markerWidth="6" markerHeight="6"
                    refX="3" refY="3" orient="auto">
                    <polygon points="0 0, 6 3, 0 6" fill="#555" />
                </marker>
            </defs>
            <line
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#555" strokeWidth={1.5}
                markerEnd="url(#arrowhead)"
            />
            {/* Label */}
            <text
                x={x3} y={y3}
                textAnchor={x3 > cx ? 'start' : 'end'}
                dominantBaseline="central"
                style={{ fontSize: '11px', fontWeight: 600, fill: '#333' }}
            >
                {name}
            </text>
            <text
                x={x3} y={y3 + 13}
                textAnchor={x3 > cx ? 'start' : 'end'}
                dominantBaseline="central"
                style={{ fontSize: '10px', fill: '#888' }}
            >
                {(percent * 100).toFixed(0)}%
            </text>
        </g>
    );
};

const DashboardGeoChart = () => {
    const [geoData, setGeoData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getGeoData();
    }, []);

    const getGeoData = () => {
        fetchAllDataAPI('sub-get-dashboard-geo-chart-data')
            .then((res) => {
                if (res.data.status === 1) {
                    setGeoData(res.data.geodata);
                } else {
                    setFallback();
                }
            })
            .catch(() => {
                toast.warning("Could not load geo data");
                setFallback();
            })
            .finally(() => setLoading(false));
    }

    const setFallback = () => {
        setGeoData([
            { state: 'Delhi', orders: 35 },
            { state: 'Mumbai', orders: 25 },
            { state: 'Bangalore', orders: 18 },
            { state: 'Haryana', orders: 12 },
            { state: 'Punjab', orders: 10 },
        ]);
    }

    const totalOrders = geoData.reduce((sum, item) => sum + item.orders, 0);

    return (
        <div className="card radius-10 p-3 w-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0" style={{ fontWeight: 600 }}>📍 Orders by Location</h6>
                <span style={{ fontSize: "12px", color: "#888" }}>Top States</span>
            </div>

            {loading ? (
                <div className="text-center py-4">Loading map data...</div>
            ) : (
                <>
                    {/* Donut Chart with Arrow Labels */}
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
                            <Pie
                                data={geoData}
                                dataKey="orders"
                                nameKey="state"
                                cx="50%"
                                cy="50%"
                                outerRadius={85}
                                innerRadius={42}
                                labelLine={false}
                                label={renderCustomLabel}
                            >
                                {geoData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={COLORS[index % COLORS.length]}
                                    />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{ borderRadius: "8px", fontSize: "13px" }}
                                formatter={(value, name) => [`${value} orders`, name]}
                            />
                        </PieChart>
                    </ResponsiveContainer>

                    {/* State List with Progress Bar */}
                    <div className="mt-2">
                        {geoData.map((item, index) => (
                            <div key={index}
                                className="d-flex justify-content-between align-items-center mb-2">
                                <div className="d-flex align-items-center">
                                    <span style={{
                                        width: "10px", height: "10px",
                                        borderRadius: "50%",
                                        background: COLORS[index % COLORS.length],
                                        display: "inline-block",
                                        marginRight: "8px"
                                    }} />
                                    <span style={{ fontSize: "13px" }}>{item.state}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div style={{
                                        width: "80px", height: "6px",
                                        background: "#f0f0f0", borderRadius: "3px",
                                        overflow: "hidden", marginRight: "8px"
                                    }}>
                                        <div style={{
                                            width: `${(item.orders / totalOrders) * 100}%`,
                                            height: "100%",
                                            background: COLORS[index % COLORS.length],
                                            borderRadius: "3px"
                                        }} />
                                    </div>
                                    <span style={{ fontSize: "12px", color: "#888", minWidth: "30px" }}>
                                        {item.orders}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

export default DashboardGeoChart